import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "react-toastify";
import Navbar from "../components/ui/Navbar";
import CronogramaCalendar from "../components/ui/CronogramaCalendar";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  Bot,
  AlertTriangle,
  Building2,
  ClipboardList,
  CircleAlert,
  CircleCheckBig,
  Clock3,
  ChevronDown,
  ChevronUp,
  LoaderPinwheel,
  MonitorCheck,
  Package,
  ShieldAlert,
  Sparkles,
  Users,
  Wrench,
} from "lucide-react";
import DataTable from "react-data-table-component";
import { requestPermissionAndToken } from "../firebase";
import { DASHBOARD_METRIC_SOURCES } from "../utils/dashboardPreferences";
import { useDashboardLayout } from "../hooks/useDashboardLayout";
import { useOnboarding } from "../hooks/useOnboarding";
import OnboardingWizardModal from "../components/onboarding/OnboardingWizardModal";
import OnboardingBanner from "../components/onboarding/OnboardingBanner";
import DashboardTour from "../components/onboarding/DashboardTour";

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#A020F0",
  "#FF4D4D",
  "#8884d8",
  "#82ca9d",
  "#ffc658",
];

const hasVisiblePrioridades = (payload) =>
  payload?.visible !== false &&
  Array.isArray(payload?.topPrioridades) &&
  payload.topPrioridades.length > 0;

// ── KPI card component ──────────────────────────────────────────────────────
const KpiCard = ({ icon: Icon, label, value, color, onClick, sub }) => {
  const colorMap = {
    blue: {
      bg: "bg-blue-50",
      border: "border-blue-100",
      icon: "text-blue-500 bg-blue-100",
      value: "text-blue-700",
    },
    green: {
      bg: "bg-emerald-50",
      border: "border-emerald-100",
      icon: "text-emerald-500 bg-emerald-100",
      value: "text-emerald-700",
    },
    yellow: {
      bg: "bg-amber-50",
      border: "border-amber-100",
      icon: "text-amber-500 bg-amber-100",
      value: "text-amber-700",
    },
    red: {
      bg: "bg-red-50",
      border: "border-red-100",
      icon: "text-red-500 bg-red-100",
      value: "text-red-700",
    },
    orange: {
      bg: "bg-orange-50",
      border: "border-orange-100",
      icon: "text-orange-500 bg-orange-100",
      value: "text-orange-700",
    },
    gray: {
      bg: "bg-gray-50",
      border: "border-gray-100",
      icon: "text-gray-500 bg-gray-100",
      value: "text-gray-700",
    },
  };
  const c = colorMap[color] || colorMap.gray;

  return (
    <div
      className={`rounded-xl border ${c.border} ${c.bg} p-4 flex items-start gap-4 ${onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`}
      onClick={onClick}
    >
      <div className={`rounded-lg p-2.5 ${c.icon} flex-shrink-0`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
          {label}
        </p>
        <p className={`text-2xl font-bold mt-0.5 ${c.value}`}>{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5 truncate">{sub}</p>}
      </div>
    </div>
  );
};

// ── Main Dashboard ──────────────────────────────────────────────────────────
const Dashboard = () => {
  const [data, setData] = useState({});
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [sinMantenimiento, setSinMantenimiento] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState("");
  const [sedes, setSedes] = useState([]);
  const [sedeSeleccionada, setSedeSeleccionada] = useState("");
  const [sedeGrupos, setSedeGrupos] = useState([]);
  const [sedeGrupoSeleccionado, setSedeGrupoSeleccionado] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [alarmasSummary, setAlarmasSummary] = useState(null);
  const [prioridades, setPrioridades] = useState(null);
  const [loadingPrioridades, setLoadingPrioridades] = useState(false);
  const [prioridadesAcordeonAbierto, setPrioridadesAcordeonAbierto] = useState(
    () => {
      try {
        const localUser = JSON.parse(localStorage.getItem("user") || "{}");
        const key = `prioridades_ui_${localUser?.id || localUser?._id || "anon"}`;
        const raw = localStorage.getItem(key);
        if (!raw) return true;
        const parsed = JSON.parse(raw);
        return parsed?.abierto ?? true;
      } catch {
        return true;
      }
    },
  );
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user"));
  const { layout: dashboardLayout } = useDashboardLayout();

  const {
    onboarding,
    applicableSteps,
    pendingSteps,
    shouldAutoOpenWizard,
    shouldAutoStartTour,
    patchOnboarding,
  } = useOnboarding();

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardInitialStep, setWizardInitialStep] = useState(null);
  const autoOpenTriggered = useRef(false);
  const wizardStepsDoneRef = useRef(onboarding.wizardStepsDone || []);

  // Derived locally from the ref (which updates synchronously in
  // handleStepDone) instead of from onboarding.wizardStepsDone/pendingSteps
  // (hook state, which only updates after the corresponding PATCH resolves).
  // This avoids the banner flickering back on for one render after the user
  // finishes the wizard, since wizardOpen flips to false synchronously but
  // the hook state lags behind by a network round-trip.
  const bannerPendingCount = applicableSteps.filter(
    (step) => !wizardStepsDoneRef.current.includes(step),
  ).length;
  const showBannerNow =
    !onboarding.wizardDismissed && !wizardOpen && bannerPendingCount > 0;

  useEffect(() => {
    if (shouldAutoOpenWizard && !autoOpenTriggered.current) {
      autoOpenTriggered.current = true;
      setWizardInitialStep(pendingSteps[0] || null);
      setWizardOpen(true);
      patchOnboarding({ wizardShown: true }).catch((err) =>
        console.error("No se pudo actualizar el estado del wizard:", err),
      );
    }
    // eslint-disable-next-line
  }, [shouldAutoOpenWizard]);

  const openWizard = () => {
    setWizardInitialStep(pendingSteps[0] || null);
    setWizardOpen(true);
  };

  const closeWizard = () => setWizardOpen(false);

  // wizardStepsDoneRef tracks progress synchronously and independently of
  // the useOnboarding() hook's `onboarding` state, which only updates after
  // each PATCH resolves. Computing the array to send from `onboarding` state
  // instead of this ref would silently drop steps if the user clicks through
  // the wizard faster than the network round-trip (each click's PATCH would
  // race against the previous one, and a slow response landing late could
  // overwrite a newer completion). The ref is append-only for the lifetime
  // of this page mount, seeded once from the persisted value on first render.
  const handleStepDone = (stepKey) => {
    if (!wizardStepsDoneRef.current.includes(stepKey)) {
      wizardStepsDoneRef.current = [...wizardStepsDoneRef.current, stepKey];
    }
    patchOnboarding({ wizardStepsDone: wizardStepsDoneRef.current }).catch(
      (err) =>
        console.error("No se pudo guardar el progreso del wizard:", err),
    );
  };
  const empresaBloqueadaRef = useRef(false);
  const notificationsInitializedRef = useRef(false);
  const API_URL = process.env.REACT_APP_API_URL;
  const prioridadesStorageKey = `prioridades_diarias_${user?.id || user?._id || "anon"}`;
  const prioridadesUiStorageKey = `prioridades_ui_${user?.id || user?._id || "anon"}`;

  const logoutEmpresaInactiva = () => {
    if (empresaBloqueadaRef.current) return;
    empresaBloqueadaRef.current = true;
    localStorage.clear();
    toast.error(
      "🚫 La empresa fue deshabilitada. Contacta con el administrador.",
      {
        autoClose: 4000,
      },
    );
    setTimeout(() => navigate("/login"), 500);
  };

  const savePrioridadesLocal = (payload) => {
    try {
      localStorage.setItem(
        prioridadesStorageKey,
        JSON.stringify({
          data: payload,
          updatedAt: new Date().toISOString(),
        }),
      );
    } catch {
      // noop
    }
  };

  const loadPrioridadesLocal = () => {
    try {
      const raw = localStorage.getItem(prioridadesStorageKey);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const setAcordeonPrioridades = (abierto) => {
    setPrioridadesAcordeonAbierto(abierto);
    try {
      localStorage.setItem(
        prioridadesUiStorageKey,
        JSON.stringify({ abierto, updatedAt: new Date().toISOString() }),
      );
    } catch {
      // noop
    }
  };

  async function initNotifications() {
    try {
      const { token: fcmToken, permission } = await requestPermissionAndToken();
      if (permission === "denied") {
        toast.info(
          "Las notificaciones están bloqueadas en este dispositivo. Actívalas en la configuración del navegador.",
        );
      }
      if (permission === "unsupported") return;
      if (fcmToken) {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/notifications/register`,
          {
            token: fcmToken,
            userId: user?.id,
          },
        );
      }
    } catch (error) {
      toast.error("No se pudo inicializar notificaciones:", error);
    }
  }

  useEffect(() => {
    try {
      const raw = localStorage.getItem(prioridadesUiStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setPrioridadesAcordeonAbierto(parsed?.abierto ?? true);
    } catch {
      // noop
    }
  }, [prioridadesUiStorageKey]);

  useEffect(() => {
    if (notificationsInitializedRef.current) return;
    notificationsInitializedRef.current = true;
    initNotifications();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        if (!Object.keys(data).length) setIsInitialLoading(true);

        const headers = { Authorization: `Bearer ${token}` };
        const params = {};
        if (sedeGrupoSeleccionado) {
          params.sedeGrupo = sedeGrupoSeleccionado;
        } else {
          if (clienteSeleccionado) params.cliente = clienteSeleccionado;
          if (sedeSeleccionada) params.sede = sedeSeleccionada;
        }
        const queryParams = Object.keys(params).length > 0 ? params : undefined;

        const [resDashboard, resSinMant, resAlarmas] = await Promise.all([
          axios.get(`${API_URL}/dashboard`, { headers, params: queryParams }),
          axios.get(`${API_URL}/equipos/dataHv`, {
            headers,
            params: queryParams,
          }),
          axios.get(`${API_URL}/cronograma/alarmas`, { headers }),
        ]);

        if (resDashboard.data.statusCompany === "false") {
          logoutEmpresaInactiva();
          return;
        }

        if (
          resDashboard.data.totalEquipos >=
          resDashboard.data.limites.maxEquipos * 0.75
        ) {
          toast.warning(
            "⚠️ Estás utilizando más del 75% de los equipos permitidos por tu plan",
          );
        }
        if (
          resDashboard.data.totalUsuarios >=
          resDashboard.data.limites.maxUsuarios * 0.75
        ) {
          toast.warning(
            "⚠️ Estás utilizando más del 75% de los usuarios permitidos por tu plan",
          );
        }

        setData(resDashboard.data);
        localStorage.setItem(
          "planActual",
          resDashboard.data.planActual || "FREE",
        );
        setSinMantenimiento(resSinMant.data);
        setAlarmasSummary(resAlarmas.data.summary);
      } catch (err) {
        toast.error("No se pudieron cargar los datos del dashboard");
      } finally {
        setIsLoading(false);
        setIsInitialLoading(false);
      }
    };

    if (token) fetchData();
    // eslint-disable-next-line
  }, [
    token,
    clienteSeleccionado,
    sedeSeleccionada,
    sedeGrupoSeleccionado,
    API_URL,
  ]);

  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [resClientes, resGrupos] = await Promise.all([
          axios.get(`${API_URL}/clientes`, { headers }),
          axios.get(`${API_URL}/sede-grupos`, { headers }),
        ]);
        setClientes(resClientes.data);
        setSedeGrupos(resGrupos.data || []);
      } catch (err) {
        toast.error("No se pudieron cargar los clientes");
      }
    };
    if (token) fetchClientes();
  }, [token, API_URL]);

  useEffect(() => {
    const fetchPrioridades = async () => {
      if (!token) return;
      try {
        setLoadingPrioridades(true);
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API_URL}/ia/prioridades-diarias`, {
          headers,
        });
        const payload = res.data || null;
        setPrioridades(payload);
        savePrioridadesLocal(payload);
      } catch (err) {
        const local = loadPrioridadesLocal();
        if (hasVisiblePrioridades(local?.data)) {
          setPrioridades(local.data);
        } else {
          setPrioridades(null);
        }
        toast.error(
          err?.response?.data?.message ||
            "No se pudieron cargar las prioridades de hoy",
        );
      } finally {
        setLoadingPrioridades(false);
      }
    };
    fetchPrioridades();
    // eslint-disable-next-line
  }, [token, API_URL]);

  useEffect(() => {
    const fetchSedes = async () => {
      if (!clienteSeleccionado) {
        setSedes([]);
        setSedeSeleccionada("");
        return;
      }
      try {
        setSedeSeleccionada("");
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API_URL}/sedes/${clienteSeleccionado}`, {
          headers,
        });
        setSedes(res.data || []);
      } catch (err) {
        toast.error("No se pudieron cargar las sedes");
      }
    };
    if (token) fetchSedes();
  }, [token, clienteSeleccionado, API_URL]);

  const prepareChartData = (sourceKey, maxItems = 8, groupOthers = false) => {
    const source = Array.isArray(data?.[sourceKey]) ? data[sourceKey] : [];
    const sorted = [...source].sort(
      (a, b) => (b?.total || 0) - (a?.total || 0),
    );
    const limited = sorted.slice(0, maxItems);
    if (!groupOthers) return limited;
    const otherTotal = sorted
      .slice(maxItems)
      .reduce((acc, curr) => acc + (Number(curr?.total) || 0), 0);
    return otherTotal > 0
      ? [...limited, { _id: "Otros", total: otherTotal }]
      : limited;
  };

  const renderBarChartCard = (
    title,
    sourceKey,
    color = "#8884d8",
    maxItems = 8,
    keyPrefix = sourceKey,
  ) => {
    const chartData = prepareChartData(sourceKey, maxItems);
    return (
      <Card key={`${keyPrefix}-${title}`}>
        <CardContent>
          <h2 className="text-base font-semibold text-gray-700 mb-3">
            {title}
          </h2>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData}>
              <XAxis dataKey="_id" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="total" fill={color} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    );
  };

  const renderPieChartCard = (
    title,
    sourceKey,
    maxItems = 6,
    keyPrefix = sourceKey,
  ) => {
    const chartData = prepareChartData(sourceKey, maxItems, true);
    return (
      <Card key={`${keyPrefix}-${title}`}>
        <CardContent>
          <h2 className="text-base font-semibold text-gray-700 mb-3">
            {title}
          </h2>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={chartData}
                dataKey="total"
                nameKey="_id"
                outerRadius={100}
                label={({ name, percent }) =>
                  `${name} (${(percent * 100).toFixed(0)}%)`
                }
              >
                {chartData.map((_, idx) => (
                  <Cell
                    key={`${keyPrefix}-cell-${idx}`}
                    fill={COLORS[idx % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [value, "Equipos"]} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    );
  };

  const totalAlarmas = alarmasSummary
    ? alarmasSummary.noAgendado +
      alarmasSummary.vencido +
      alarmasSummary.fueraPeriodicidad
    : null;

  const handleCrearGestionDesdeRecomendacion = (recomendacion) => {
    if (recomendacion?.cronogramaId) {
      navigate(`/manage/${recomendacion.cronogramaId}`);
      return;
    }
    toast.info(
      "Esta recomendación no tiene un cronograma asociado. Te llevamos a Servicios para crear la gestión.",
    );
    navigate("/services");
  };

  const prioridadesPendientes = prioridades?.topPrioridades || [];

  const shouldShowPrioridadesWidget =
    hasVisiblePrioridades(prioridades) && prioridadesPendientes.length > 0;

  const blockHasData = (id) => {
    switch (id) {
      case "estado-mantenimiento":
        return alarmasSummary !== null;
      case "prioridades-ia":
        return loadingPrioridades || shouldShowPrioridadesWidget;
      case "tabla-alertas-stock":
        return (data.stockAlertas?.length || 0) > 0;
      case "tabla-categorias-sin-rutina":
        return (data.categoriasSinRutina?.length || 0) > 0;
      case "tabla-sin-hoja-vida":
        return sinMantenimiento.length > 0;
      default:
        return true;
    }
  };

  const renderCustomChartBlock = (id) => {
    const chart = (dashboardLayout.customCharts || []).find((c) => c.id === id);
    if (!chart) return null;
    const sourceMeta = DASHBOARD_METRIC_SOURCES.find(
      (s) => s.key === chart.source,
    );
    if (!sourceMeta) return null;
    if (chart.type === "pie") {
      return renderPieChartCard(
        `🧠 ${chart.title}`,
        chart.source,
        Number(chart.maxItems) || 8,
        chart.id,
      );
    }
    return renderBarChartCard(
      `🧠 ${chart.title}`,
      chart.source,
      "#2563eb",
      Number(chart.maxItems) || 8,
      chart.id,
    );
  };

  const renderBlock = (id) => {
    switch (id) {
      case "kpi-clientes":
        return (
          <KpiCard
            key={id}
            icon={Building2}
            label="Clientes registrados"
            value={data.totalClientes ?? 0}
            color="blue"
            onClick={() => navigate("/equipos")}
          />
        );
      case "kpi-equipos":
        return (
          <KpiCard
            key={id}
            icon={MonitorCheck}
            label="Equipos registrados"
            value={data.totalEquipos ?? 0}
            color="green"
            onClick={() => navigate("/equipos")}
          />
        );
      case "kpi-hojas-vida-pendientes":
        return (
          <KpiCard
            key={id}
            icon={ClipboardList}
            label="Hojas de vida pendientes"
            value={sinMantenimiento.length || 0}
            color="yellow"
            sub={
              sinMantenimiento.length
                ? "Sin hoja de vida configurada"
                : "Todo al día"
            }
            onClick={() => navigate("/config")}
          />
        );
      case "kpi-rutinas-sin-configurar":
        return (
          <KpiCard
            key={id}
            icon={Wrench}
            label="Rutinas sin configurar"
            value={data.categoriasSinRutina?.length || 0}
            color={data.categoriasSinRutina?.length ? "red" : "green"}
            onClick={() => navigate("/config")}
          />
        );
      case "kpi-servicios-por-autorizar":
        return (
          <KpiCard
            key={id}
            icon={ShieldAlert}
            label="Servicios por autorizar"
            value={data.serviciosPendientes || 0}
            color="orange"
            onClick={() => navigate("/services")}
          />
        );
      case "kpi-alertas-stock":
        return (
          <KpiCard
            key={id}
            icon={Package}
            label="Alertas de stock"
            value={data.stockAlertas?.length || 0}
            color={data.stockAlertas?.length ? "red" : "green"}
            onClick={() => navigate("/storage")}
          />
        );
      case "estado-mantenimiento":
        return (
          <div
            key={id}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate("/services", { state: { tab: "alarmas" } })}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <h2 className="font-semibold text-gray-800">
                  Estado de mantenimiento
                </h2>
              </div>
              <span className="text-xs text-blue-600 hover:underline font-medium">
                Ver alarmas →
              </span>
            </div>

            {totalAlarmas === 0 ? (
              <div className="flex items-center gap-2 text-emerald-600">
                <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                <span className="text-sm font-medium">
                  Todos los equipos al día
                </span>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg bg-red-50 border border-red-100 p-3 text-center">
                  <p className="text-2xl font-bold text-red-700">
                    {alarmasSummary.vencido}
                  </p>
                  <p className="text-xs text-red-500 mt-0.5 font-medium">
                    Vencidos
                  </p>
                </div>
                <div className="rounded-lg bg-orange-50 border border-orange-100 p-3 text-center">
                  <p className="text-2xl font-bold text-orange-700">
                    {alarmasSummary.fueraPeriodicidad}
                  </p>
                  <p className="text-xs text-orange-500 mt-0.5 font-medium">
                    Fuera de periodicidad
                  </p>
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-100 p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">
                    {alarmasSummary.noAgendado}
                  </p>
                  <p className="text-xs text-amber-500 mt-0.5 font-medium">
                    No agendados
                  </p>
                </div>
              </div>
            )}
          </div>
        );
      case "prioridades-ia":
        return (
          <Card key={id} className="border-violet-100 shadow-sm">
            <CardContent>
              <button
                type="button"
                onClick={() =>
                  setAcordeonPrioridades(!prioridadesAcordeonAbierto)
                }
                className="w-full flex items-center justify-between rounded-lg border border-violet-100 bg-violet-50 px-3 py-2.5"
              >
                <div className="flex items-center gap-2 text-left">
                  <Sparkles className="h-4 w-4 text-violet-600" />
                  <div>
                    <p className="text-sm font-semibold text-violet-900">
                      Top 5 prioridades de hoy
                    </p>
                    <p className="text-xs text-violet-700">
                      {prioridadesPendientes.length} pendiente(s) por resolver
                    </p>
                  </div>
                </div>
                {prioridadesAcordeonAbierto ? (
                  <ChevronUp className="h-4 w-4 text-violet-700" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-violet-700" />
                )}
              </button>

              {prioridadesAcordeonAbierto && (
                <>
                  {loadingPrioridades ? (
                    <div className="mt-3 flex items-center gap-2 rounded-lg border border-violet-100 bg-violet-50 px-4 py-3 text-sm text-violet-700">
                      <LoaderPinwheel className="h-4 w-4 animate-spin" />
                      Cargando tus prioridades asignadas...
                    </div>
                  ) : (
                    <div className="space-y-4 mt-3">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <p className="text-sm text-gray-500">
                          Basado únicamente en tus servicios asignados vencidos
                          o próximos.
                        </p>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 px-2.5 py-1">
                            <CircleAlert className="h-3.5 w-3.5" />
                            Alta urgencia
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 px-2.5 py-1">
                            <Clock3 className="h-3.5 w-3.5" />
                            Próximo a vencer
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {prioridadesPendientes.slice(0, 5).map((p, idx) => (
                          <div
                            key={`${p?.equipoId || "equipo"}-${idx}`}
                            className="rounded-xl border border-gray-200 bg-white p-4 hover:shadow-sm transition-shadow"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-center gap-2">
                                <span className="h-7 w-7 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center">
                                  {idx + 1}
                                </span>
                                <p className="text-sm font-semibold text-gray-900">
                                  {p?.titulo || `Prioridad ${idx + 1}`}
                                </p>
                              </div>
                              <span
                                className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                                  (p?.nivelPrioridad || "").toLowerCase() ===
                                  "alta"
                                    ? "bg-red-50 text-red-700"
                                    : "bg-amber-50 text-amber-700"
                                }`}
                              >
                                {p?.nivelPrioridad || "Media"}
                              </span>
                            </div>

                            <div className="mt-3 space-y-2 text-xs text-gray-600">
                              <p>
                                <span className="font-semibold text-gray-700">
                                  ¿Por qué es prioridad?
                                </span>{" "}
                                {p?.justificacion ||
                                  prioridades?.justificacion ||
                                  "Sin detalle"}
                              </p>
                              <p>
                                <span className="font-semibold text-gray-700">
                                  ¿Qué hacer ahora?
                                </span>{" "}
                                {p?.accionSugerida ||
                                  prioridades?.accionSugerida ||
                                  "Sin detalle"}
                              </p>
                              <p>
                                <span className="font-semibold text-gray-700">
                                  Si no se atiende:
                                </span>{" "}
                                {p?.impactoSiNoSeAtiende ||
                                  prioridades?.impactoSiNoSeAtiende ||
                                  "Sin detalle"}
                              </p>
                            </div>

                            <Button
                              size="sm"
                              className="mt-4 w-full"
                              onClick={() =>
                                handleCrearGestionDesdeRecomendacion(p)
                              }
                            >
                              <CircleCheckBig className="h-4 w-4 mr-1" />
                              Crear gestión ahora
                            </Button>
                          </div>
                        ))}
                      </div>

                      <div className="rounded-lg bg-violet-50 border border-violet-100 p-3 text-xs text-violet-800 flex items-start gap-2">
                        <Bot className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <p>
                          Estas prioridades se mantienen activas hasta que
                          gestiones el equipo. Al cerrar una gestión, la IA
                          genera un reemplazo automáticamente.
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        );
      case "chart-cronograma":
        return <CronogramaCalendar key={id} />;
      case "chart-equipos-categoria":
        return renderBarChartCard(
          "Equipos por categoría",
          "equiposPorCategoria",
          "#8884d8",
        );
      case "chart-equipos-marca":
        return renderBarChartCard(
          "Equipos por marca",
          "equiposPorMarca",
          "#82ca9d",
        );
      case "chart-equipos-modelo":
        return renderBarChartCard(
          "Equipos por modelo",
          "equiposPorModelo",
          "#A020F0",
        );
      case "chart-equipos-area":
        return renderPieChartCard("Equipos por área", "equiposPorArea");
      case "chart-equipos-proveedor":
        return renderBarChartCard(
          "Equipos por proveedor",
          "equiposPorProveedor",
          "#ffc658",
        );
      case "tabla-alertas-stock":
        return (
          <Card key={id}>
            <CardContent>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                  <Package className="h-4 w-4 text-red-500" />
                  Alertas de stock mínimo
                </h2>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate("/storage")}
                >
                  Ver almacenes
                </Button>
              </div>
              <DataTable
                columns={[
                  {
                    name: "Almacén",
                    selector: (row) => row.almacenNombre || "—",
                    sortable: true,
                  },
                  {
                    name: "Artículo",
                    selector: (row) => row.articulo || "—",
                    sortable: true,
                  },
                  {
                    name: "Código",
                    selector: (row) => row.codigo_item || "—",
                    sortable: true,
                  },
                  {
                    name: "Stock actual",
                    selector: (row) => row.stock_actual ?? 0,
                    sortable: true,
                  },
                  {
                    name: "Mínimo",
                    selector: (row) => row.stock_minimo ?? "—",
                    sortable: true,
                  },
                  {
                    name: "Máximo",
                    selector: (row) => row.stock_maximo ?? "—",
                    sortable: true,
                  },
                ]}
                data={data.stockAlertas}
                pagination
                highlightOnHover
                dense
              />
            </CardContent>
          </Card>
        );
      case "tabla-categorias-sin-rutina":
        return (
          <Card key={id}>
            <CardContent>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-amber-500" />
                  Categorías sin rutina registrada
                </h2>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate("/config")}
                >
                  Configurar rutinas
                </Button>
              </div>
              <DataTable
                columns={[
                  {
                    name: "Categoría",
                    selector: (row) => row.name,
                    sortable: true,
                  },
                ]}
                data={data.categoriasSinRutina}
                pagination
                highlightOnHover
                dense
              />
            </CardContent>
          </Card>
        );
      case "tabla-sin-hoja-vida":
        return (
          <Card key={id}>
            <CardContent>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                  <Users className="h-4 w-4 text-amber-500" />
                  Marcas y modelos sin hoja de vida
                </h2>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate("/config")}
                >
                  Configurar
                </Button>
              </div>
              <DataTable
                columns={[
                  {
                    name: "Marca",
                    selector: (row) => row.marca?.name,
                    sortable: true,
                  },
                  {
                    name: "Modelo",
                    selector: (row) => row.modelo?.name,
                    sortable: true,
                  },
                ]}
                data={sinMantenimiento}
                pagination
                highlightOnHover
                dense
              />
            </CardContent>
          </Card>
        );
      default:
        if (id.startsWith("custom-")) return renderCustomChartBlock(id);
        return null;
    }
  };

  const visibleBlocks = (dashboardLayout.orderedBlocks || []).filter(
    (b) => b.visible && blockHasData(b.id),
  );

  const blockGroups = [];
  for (const b of visibleBlocks) {
    const last = blockGroups[blockGroups.length - 1];
    if (last && last.family === b.family) last.items.push(b);
    else blockGroups.push({ family: b.family, items: [b] });
  }

  const FAMILY_WRAPPER_CLASS = {
    kpi: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4",
    chart: "grid grid-cols-1 lg:grid-cols-2 gap-6",
    section: "space-y-6",
    table: "space-y-6",
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-6 pt-20 md:pt-28 space-y-6">
        {showBannerNow && (
          <OnboardingBanner
            pendingCount={bannerPendingCount}
            totalCount={applicableSteps.length}
            onContinue={openWizard}
            onDismiss={() =>
              patchOnboarding({ wizardDismissed: true }).catch((err) =>
                console.error(
                  "No se pudo descartar el banner de onboarding:",
                  err,
                ),
              )
            }
          />
        )}

        {/* Loading banner */}
        {isInitialLoading && (
          <div className="flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
            <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600 flex-shrink-0" />
            Cargando información del dashboard, por favor espera...
          </div>
        )}

        {/* Header: plan + filtros */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400 font-medium">
                  Plan actual
                </p>
                <span className="inline-block mt-0.5 px-3 py-0.5 rounded-full text-sm font-bold bg-blue-100 text-blue-700">
                  {data.planActual ?? "FREE"}
                </span>
              </div>
              <div className="h-10 w-px bg-gray-200 hidden md:block" />
              <div>
                <p className="text-xs text-gray-400">Usuarios</p>
                <p className="font-semibold text-gray-700">
                  {data.totalUsuarios ?? 0}{" "}
                  <span className="text-gray-400 font-normal text-sm">
                    / {data.limites?.maxUsuarios ?? 0}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Equipos</p>
                <p className="font-semibold text-gray-700">
                  {data.totalEquipos ?? 0}{" "}
                  <span className="text-gray-400 font-normal text-sm">
                    / {data.limites?.maxEquipos ?? 0}
                  </span>
                </p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row flex-wrap gap-3">
              {sedeGrupos.length > 0 && (
                <select
                  value={sedeGrupoSeleccionado}
                  onChange={(e) => {
                    setSedeGrupoSeleccionado(e.target.value);
                    if (e.target.value) {
                      setClienteSeleccionado("");
                      setSedeSeleccionada("");
                    }
                  }}
                  className="flex-1 min-w-[180px] border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                >
                  <option value="">Grupo de sedes...</option>
                  {sedeGrupos.map((g) => (
                    <option key={g._id} value={g._id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              )}

              <select
                value={clienteSeleccionado}
                onChange={(e) => {
                  setClienteSeleccionado(e.target.value);
                  setSedeGrupoSeleccionado("");
                }}
                className="flex-1 min-w-[180px] border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-blue-400 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
                disabled={Boolean(sedeGrupoSeleccionado)}
              >
                <option value="">Todos los clientes</option>
                {clientes.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </select>

              <select
                value={sedeSeleccionada}
                onChange={(e) => setSedeSeleccionada(e.target.value)}
                className="flex-1 min-w-[180px] border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-blue-400 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
                disabled={
                  !clienteSeleccionado || Boolean(sedeGrupoSeleccionado)
                }
              >
                <option value="">Todas las sedes</option>
                {sedes.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name}
                  </option>
                ))}
              </select>

              {isLoading && !isInitialLoading && (
                <div className="flex items-center gap-1.5 text-xs text-blue-500 whitespace-nowrap">
                  <LoaderPinwheel className="h-3.5 w-3.5 animate-spin" />
                  Actualizando...
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bloques del dashboard segun el layout personalizado */}
        {visibleBlocks.length === 0 ? (
          <Card>
            <CardContent>
              <p className="text-gray-600">
                No hay elementos visibles en tu dashboard. Ve a configuracion y
                activa los que necesites.
              </p>
              <Button className="mt-3" onClick={() => navigate("/config")}>
                Ir a configuracion del dashboard
              </Button>
            </CardContent>
          </Card>
        ) : (
          blockGroups.map((group, groupIndex) => {
            const firstKpiGroup =
              group.family === "kpi" &&
              !blockGroups
                .slice(0, groupIndex)
                .some((g) => g.family === "kpi");
            const firstChartGroup =
              group.family === "chart" &&
              !blockGroups
                .slice(0, groupIndex)
                .some((g) => g.family === "chart");
            return (
              <div
                key={`block-group-${groupIndex}`}
                className={FAMILY_WRAPPER_CLASS[group.family]}
                data-tour={
                  firstKpiGroup
                    ? "dashboard-kpis"
                    : firstChartGroup
                      ? "dashboard-charts"
                      : undefined
                }
              >
                {group.items.map((b) => renderBlock(b.id))}
              </div>
            );
          })
        )}
      </div>

      <OnboardingWizardModal
        open={wizardOpen}
        initialStep={wizardInitialStep}
        applicableSteps={applicableSteps}
        onClose={closeWizard}
        onStepDone={handleStepDone}
      />

      <DashboardTour
        run={shouldAutoStartTour && !wizardOpen}
        onFinish={() =>
          patchOnboarding({ tourCompleted: true }).catch((err) =>
            console.error("No se pudo registrar el fin del tour:", err),
          )
        }
      />
    </div>
  );
};

export default Dashboard;
